/**
 * QueryAnalyticsTab.jsx - Admin dashboard for query assessment analytics
 * 
 * Features:
 * - Combined overview + recent queries view
 * - User tracking for each query
 * - Grade distribution charts
 * - Top issues and what's working
 * - AI recommendations
 * - Query performance trends
 */

import React, { useState, useEffect, useCallback } from 'react';
import { View, Text, TouchableOpacity, ScrollView, StyleSheet, ActivityIndicator } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { theme } from '../constants/theme';
import Constants from 'expo-constants';

const API_BASE = Constants.expoConfig?.extra?.API_BASE || 'https://us-central1-bet-optimized-fe922.cloudfunctions.net';

const QueryAnalyticsTab = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dateRange, setDateRange] = useState(14); // days
  const [activeSection, setActiveSection] = useState('overview'); // 'overview', 'issues', 'trends'
  
  // Data states
  const [analysis, setAnalysis] = useState(null);
  const [assessments, setAssessments] = useState([]);
  const [lowScoring, setLowScoring] = useState([]);
  const [dailySummaries, setDailySummaries] = useState([]);
  
  // Fetch all data
  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const [analysisRes, assessmentsRes, lowRes, dailyRes] = await Promise.all([
        fetch(`${API_BASE}/queryAssessments`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ action: 'analysis', days: dateRange })
        }),
        fetch(`${API_BASE}/queryAssessments`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ action: 'list', days: dateRange, limit: 50 })
        }),
        fetch(`${API_BASE}/queryAssessments`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ action: 'low-scoring', days: dateRange })
        }),
        fetch(`${API_BASE}/queryAssessments`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ action: 'daily', days: dateRange })
        })
      ]);
      
      const [analysisData, assessmentsData, lowData, dailyData] = await Promise.all([
        analysisRes.json(),
        assessmentsRes.json(),
        lowRes.json(),
        dailyRes.json()
      ]);
      
      setAnalysis(analysisData.data);
      setAssessments(Array.isArray(assessmentsData.data) ? assessmentsData.data : []);
      setLowScoring(Array.isArray(lowData.data) ? lowData.data : []);
      setDailySummaries(Array.isArray(dailyData.data) ? dailyData.data : []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [dateRange]);
  
  useEffect(() => {
    fetchData();
  }, [fetchData]);
  
  const getScoreColor = (score) => {
    if (score >= 8) return '#22C55E';
    if (score >= 6) return '#F59E0B';
    if (score >= 4) return '#F97316';
    return '#EF4444';
  };
  
  const getGradeColor = (grade) => {
    if (grade?.startsWith('A')) return '#22C55E';
    if (grade?.startsWith('B')) return '#3B82F6';
    if (grade?.startsWith('C')) return '#F59E0B';
    return '#EF4444';
  };
  
  const formatTimestamp = (timestamp) => {
    if (!timestamp) return '';
    let date;
    if (timestamp._seconds) {
      date = new Date(timestamp._seconds * 1000);
    } else if (timestamp.seconds) {
      date = new Date(timestamp.seconds * 1000);
    } else if (typeof timestamp === 'string') {
      date = new Date(timestamp);
    } else {
      date = new Date(timestamp);
    }
    if (isNaN(date.getTime())) return '';
    
    const now = new Date();
    const diff = now - date;
    const mins = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);
    
    if (mins < 1) return 'Just now';
    if (mins < 60) return `${mins}m ago`;
    if (hours < 24) return `${hours}h ago`;
    if (days < 7) return `${days}d ago`;
    
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };
  
  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return dateStr;
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };
  
  const getUserDisplay = (user) => {
    if (!user) return 'Anonymous';
    if (user.name) return user.name;
    if (user.email) {
      const atIndex = user.email.indexOf('@');
      return atIndex > 0 ? user.email.substring(0, atIndex) : user.email;
    }
    if (user.id) return `User ${user.id.substring(0, 6)}...`;
    return 'Anonymous';
  };

  // Render a single assessment card with full details
  const renderAssessmentCard = (a, index) => {
    const resultCount = a.resultsSnapshot?.totalCount || a.resultCount || 0;
    const timing = a.timingBreakdown?.total || a.timing?.total || 0;
    const timingDisplay = timing > 1000 ? `${(timing/1000).toFixed(1)}s` : `${timing}ms`;
    const cacheSource = a.routing?.cacheSource || (a.cacheHit ? 'cache' : 'live');
    
    return (
      <View key={a.id || index} style={styles.queryCard}>
        {/* Header Row: Score + Grade + Time */}
        <View style={styles.queryHeader}>
          <View style={styles.queryScores}>
            <View style={[styles.scorePill, { backgroundColor: getScoreColor(a.autoScore) + '25' }]}>
              <Text style={[styles.scoreText, { color: getScoreColor(a.autoScore) }]}>
                {a.autoScore?.toFixed(1) || '—'}
              </Text>
            </View>
            {a.grade && (
              <View style={[styles.gradePill, { backgroundColor: getGradeColor(a.grade) + '25' }]}>
                <Text style={[styles.gradeText, { color: getGradeColor(a.grade) }]}>{a.grade}</Text>
              </View>
            )}
            {a.adminScore && (
              <View style={styles.adminPill}>
                <Ionicons name="person" size={10} color="#A855F7" />
                <Text style={styles.adminPillText}>{a.adminScore}</Text>
              </View>
            )}
          </View>
          <Text style={styles.queryTime}>{formatTimestamp(a.timestamp)}</Text>
        </View>
        
        {/* Query Text */}
        <Text style={styles.queryText} numberOfLines={2}>"{a.query}"</Text>
        
        {/* Meta Row: User + Type + Results + Speed + Source */}
        <View style={styles.queryMeta}>
          <View style={styles.metaItem}>
            <Ionicons name="person-outline" size={12} color={theme.colors.textTertiary} />
            <Text style={styles.metaText}>{getUserDisplay(a.user)}</Text>
          </View>
          <View style={styles.metaDivider} />
          <View style={styles.metaItem}>
            <Ionicons name="search-outline" size={12} color={theme.colors.textTertiary} />
            <Text style={styles.metaText}>{a.queryType || 'unknown'}</Text>
          </View>
          <View style={styles.metaDivider} />
          <View style={styles.metaItem}>
            <Ionicons name="layers-outline" size={12} color={theme.colors.textTertiary} />
            <Text style={styles.metaText}>{resultCount} results</Text>
          </View>
          <View style={styles.metaDivider} />
          <View style={styles.metaItem}>
            <Ionicons name="timer-outline" size={12} color={timing > 10000 ? '#F59E0B' : theme.colors.textTertiary} />
            <Text style={[styles.metaText, timing > 10000 && { color: '#F59E0B' }]}>{timingDisplay}</Text>
          </View>
          {cacheSource && (
            <>
              <View style={styles.metaDivider} />
              <View style={[styles.cacheTag, cacheSource.includes('cache') && styles.cacheTagHit]}>
                <Ionicons name={cacheSource.includes('cache') ? 'flash' : 'cloud'} size={10} color={cacheSource.includes('cache') ? '#22C55E' : '#6B7280'} />
                <Text style={[styles.cacheText, cacheSource.includes('cache') && { color: '#22C55E' }]}>
                  {cacheSource.includes('cache') ? 'cached' : 'live'}
                </Text>
              </View>
            </>
          )}
        </View>
        
        {/* Issues/Positives Row */}
        {(a.issues?.length > 0 || a.positives?.length > 0) && (
          <View style={styles.feedbackRow}>
            {a.issues?.slice(0, 2).map((issue, i) => (
              <View key={`issue-${i}`} style={styles.issueChip}>
                <Ionicons name="alert-circle" size={10} color="#F59E0B" />
                <Text style={styles.issueChipText} numberOfLines={1}>{issue}</Text>
              </View>
            ))}
            {a.positives?.slice(0, 1).map((pos, i) => (
              <View key={`pos-${i}`} style={styles.positiveChip}>
                <Ionicons name="checkmark-circle" size={10} color="#22C55E" />
                <Text style={styles.positiveChipText} numberOfLines={1}>{pos}</Text>
              </View>
            ))}
          </View>
        )}
        
        {/* Admin Notes */}
        {a.adminNotes && (
          <View style={styles.adminNotesBox}>
            <Text style={styles.adminNotesLabel}>Admin Notes:</Text>
            <Text style={styles.adminNotesText}>{a.adminNotes}</Text>
          </View>
        )}
      </View>
    );
  };
  
  // Render overview section (combined with queries list)
  const renderOverview = () => (
    <View style={styles.section}>
      {/* Summary Stats */}
      <View style={styles.statsRow}>
        <View style={styles.statBox}>
          <Text style={styles.statNumber}>{analysis?.totalQueries || 0}</Text>
          <Text style={styles.statLabel}>Queries</Text>
        </View>
        <View style={[styles.statBox, { borderColor: getScoreColor(analysis?.avgScore || 0) + '50' }]}>
          <Text style={[styles.statNumber, { color: getScoreColor(analysis?.avgScore || 0) }]}>
            {analysis?.avgScore?.toFixed(1) || '—'}
          </Text>
          <Text style={styles.statLabel}>Avg Score</Text>
        </View>
        <View style={styles.statBox}>
          <Text style={styles.statNumber}>{analysis?.adminScored || 0}</Text>
          <Text style={styles.statLabel}>Reviewed</Text>
        </View>
      </View>
      
      {/* Grade Distribution */}
      {analysis?.gradeDistribution && Object.keys(analysis.gradeDistribution).some(k => analysis.gradeDistribution[k] > 0) && (
        <View style={styles.card}>
          <Text style={styles.cardTitle}>Grade Distribution</Text>
          <View style={styles.gradeBar}>
            {['A+', 'A', 'B+', 'B', 'C', 'D', 'F'].map(grade => {
              const count = analysis.gradeDistribution[grade] || 0;
              const total = analysis.totalQueries || 1;
              const pct = (count / total) * 100;
              if (pct === 0) return null;
              return (
                <View 
                  key={grade} 
                  style={[styles.gradeSegment, { width: `${pct}%`, backgroundColor: getGradeColor(grade) }]}
                >
                  {pct > 12 && <Text style={styles.gradeSegmentText}>{grade}</Text>}
                </View>
              );
            })}
          </View>
          <View style={styles.gradeLegend}>
            {['A+', 'A', 'B+', 'B', 'C', 'D', 'F'].map(grade => {
              const count = analysis.gradeDistribution[grade] || 0;
              if (count === 0) return null;
              return (
                <View key={grade} style={styles.legendItem}>
                  <View style={[styles.legendDot, { backgroundColor: getGradeColor(grade) }]} />
                  <Text style={styles.legendText}>{grade}: {count}</Text>
                </View>
              );
            })}
          </View>
        </View>
      )}
      
      {/* Quick Insights Row */}
      <View style={styles.insightsRow}>
        {/* Top Issues */}
        {analysis?.topIssues?.length > 0 && (
          <View style={[styles.insightCard, styles.insightCardIssues]}>
            <View style={styles.insightHeader}>
              <Ionicons name="alert-circle" size={16} color="#F59E0B" />
              <Text style={styles.insightTitle}>Top Issues</Text>
            </View>
            {analysis.topIssues.slice(0, 3).map((item, i) => (
              <View key={i} style={styles.insightRow}>
                <Text style={styles.insightText} numberOfLines={1}>{item.issue}</Text>
                <Text style={styles.insightPct}>{item.percentage}%</Text>
              </View>
            ))}
          </View>
        )}
        
        {/* What's Working */}
        {analysis?.topPositives?.length > 0 && (
          <View style={[styles.insightCard, styles.insightCardPositives]}>
            <View style={styles.insightHeader}>
              <Ionicons name="checkmark-circle" size={16} color="#22C55E" />
              <Text style={styles.insightTitle}>What's Working</Text>
            </View>
            {analysis.topPositives.slice(0, 3).map((item, i) => (
              <View key={i} style={styles.insightRow}>
                <Text style={styles.insightText} numberOfLines={1}>{item.positive}</Text>
                <Text style={[styles.insightPct, { color: '#22C55E' }]}>{item.percentage}%</Text>
              </View>
            ))}
          </View>
        )}
      </View>
      
      {/* AI Recommendations */}
      {analysis?.recommendations?.length > 0 && (
        <View style={styles.aiCard}>
          <View style={styles.aiHeader}>
            <Ionicons name="bulb" size={18} color="#A855F7" />
            <Text style={styles.aiTitle}>AI Recommendations</Text>
          </View>
          {analysis.recommendations.map((rec, i) => (
            <Text key={i} style={styles.aiText}>• {rec}</Text>
          ))}
        </View>
      )}
      
      {/* Recent Queries Section */}
      <View style={styles.recentSection}>
        <View style={styles.recentHeader}>
          <Text style={styles.recentTitle}>Recent Queries</Text>
          <Text style={styles.recentCount}>{assessments.length} total</Text>
        </View>
        
        {assessments.length === 0 ? (
          <View style={styles.emptyState}>
            <Ionicons name="search" size={32} color={theme.colors.textTertiary} />
            <Text style={styles.emptyText}>No queries recorded yet</Text>
            <Text style={styles.emptySubtext}>Queries will appear here as users search</Text>
          </View>
        ) : (
          <View style={styles.queryList}>
            {assessments.slice(0, 20).map((a, i) => renderAssessmentCard(a, i))}
          </View>
        )}
        
        {assessments.length > 20 && (
          <TouchableOpacity style={styles.showMoreBtn}>
            <Text style={styles.showMoreText}>Show More ({assessments.length - 20} more)</Text>
            <Ionicons name="chevron-down" size={16} color="#A855F7" />
          </TouchableOpacity>
        )}
      </View>
    </View>
  );
  
  // Render low scoring queries (Issues tab)
  const renderIssues = () => (
    <View style={styles.section}>
      <View style={styles.sectionHeader}>
        <Text style={styles.sectionTitle}>Queries Needing Attention</Text>
        <Text style={styles.sectionSubtitle}>Scored below 5/10</Text>
      </View>
      
      {lowScoring.length === 0 ? (
        <View style={styles.emptyStateGood}>
          <Ionicons name="checkmark-circle" size={48} color="#22C55E" />
          <Text style={styles.emptyStateTitle}>All Clear!</Text>
          <Text style={styles.emptyStateText}>No low-scoring queries in this period</Text>
        </View>
      ) : (
        <View style={styles.queryList}>
          {lowScoring.map((a, i) => renderAssessmentCard(a, i))}
        </View>
      )}
    </View>
  );
  
  // Render trends
  const renderTrends = () => (
    <View style={styles.section}>
      <Text style={styles.sectionTitle}>Daily Performance</Text>
      
      {dailySummaries.length === 0 ? (
        <Text style={styles.emptyText}>No trend data available yet</Text>
      ) : (
        <>
          {/* Mini Chart */}
          <View style={styles.chartContainer}>
            {dailySummaries.slice(0, 14).reverse().map((day, i) => {
              const score = day.avgAutoScore || 0;
              const height = Math.max(8, (score / 10) * 80);
              return (
                <View key={day.date || i} style={styles.chartBar}>
                  <View style={[styles.chartBarFill, { height, backgroundColor: getScoreColor(score) }]} />
                  <Text style={styles.chartBarScore}>{score?.toFixed(1)}</Text>
                  <Text style={styles.chartBarDate}>{day.date?.slice(-5)}</Text>
                </View>
              );
            })}
          </View>
          
          {/* Daily List */}
          <View style={styles.dailyList}>
            {dailySummaries.slice(0, 7).map((day, i) => (
              <View key={day.date || i} style={styles.dailyRow}>
                <Text style={styles.dailyDate}>{formatDate(day.date)}</Text>
                <View style={styles.dailyStats}>
                  <Text style={styles.dailyQueries}>{day.totalQueries || 0} queries</Text>
                  <View style={[styles.dailyScorePill, { backgroundColor: getScoreColor(day.avgAutoScore || 0) + '20' }]}>
                    <Text style={[styles.dailyScoreText, { color: getScoreColor(day.avgAutoScore || 0) }]}>
                      {day.avgAutoScore?.toFixed(1) || '—'}
                    </Text>
                  </View>
                </View>
              </View>
            ))}
          </View>
        </>
      )}
    </View>
  );
  
  if (loading) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color="#A855F7" />
        <Text style={styles.loadingText}>Loading analytics...</Text>
      </View>
    );
  }
  
  if (error) {
    return (
      <View style={styles.errorContainer}>
        <Ionicons name="alert-circle" size={48} color="#EF4444" />
        <Text style={styles.errorText}>{error}</Text>
        <TouchableOpacity style={styles.retryButton} onPress={fetchData}>
          <Text style={styles.retryButtonText}>Retry</Text>
        </TouchableOpacity>
      </View>
    );
  }
  
  return (
    <ScrollView style={styles.container} contentContainerStyle={styles.content}>
      {/* Header */}
      <View style={styles.header}>
        <Text style={styles.title}>Query Analytics</Text>
        <Text style={styles.subtitle}>
          {analysis?.summary || `${analysis?.totalQueries || 0} queries analyzed`}
        </Text>
      </View>
      
      {/* Date Range + Refresh */}
      <View style={styles.controls}>
        <View style={styles.dateButtons}>
          {[7, 14, 30].map(days => (
            <TouchableOpacity
              key={days}
              style={[styles.dateBtn, dateRange === days && styles.dateBtnActive]}
              onPress={() => setDateRange(days)}
            >
              <Text style={[styles.dateBtnText, dateRange === days && styles.dateBtnTextActive]}>
                {days}d
              </Text>
            </TouchableOpacity>
          ))}
        </View>
        <TouchableOpacity style={styles.refreshBtn} onPress={fetchData}>
          <Ionicons name="refresh" size={18} color={theme.colors.textSecondary} />
        </TouchableOpacity>
      </View>
      
      {/* Section Tabs */}
      <View style={styles.tabs}>
        {[
          { id: 'overview', label: 'Overview', icon: 'analytics' },
          { id: 'issues', label: 'Issues', icon: 'alert-circle', count: lowScoring.length },
          { id: 'trends', label: 'Trends', icon: 'trending-up' }
        ].map(tab => (
          <TouchableOpacity
            key={tab.id}
            style={[styles.tab, activeSection === tab.id && styles.tabActive]}
            onPress={() => setActiveSection(tab.id)}
          >
            <Ionicons 
              name={tab.icon} 
              size={16} 
              color={activeSection === tab.id ? '#A855F7' : theme.colors.textSecondary} 
            />
            <Text style={[styles.tabText, activeSection === tab.id && styles.tabTextActive]}>
              {tab.label}
            </Text>
            {tab.count > 0 && (
              <View style={styles.tabBadge}>
                <Text style={styles.tabBadgeText}>{tab.count}</Text>
              </View>
            )}
          </TouchableOpacity>
        ))}
      </View>
      
      {/* Content */}
      {activeSection === 'overview' && renderOverview()}
      {activeSection === 'issues' && renderIssues()}
      {activeSection === 'trends' && renderTrends()}
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: 'transparent'
  },
  content: {
    padding: 16,
    paddingBottom: 40
  },
  loadingContainer: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    padding: 40
  },
  loadingText: {
    color: theme.colors.textSecondary,
    marginTop: 12
  },
  errorContainer: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    padding: 40
  },
  errorText: {
    color: '#EF4444',
    marginTop: 12,
    marginBottom: 20,
    textAlign: 'center'
  },
  retryButton: {
    paddingHorizontal: 20,
    paddingVertical: 10,
    backgroundColor: 'rgba(168, 85, 247, 0.2)',
    borderRadius: 8
  },
  retryButtonText: {
    color: '#A855F7',
    fontWeight: '600'
  },
  header: {
    marginBottom: 16
  },
  title: {
    fontSize: 22,
    fontWeight: '700',
    color: theme.colors.textPrimary,
    marginBottom: 4
  },
  subtitle: {
    fontSize: 14,
    color: theme.colors.textSecondary
  },
  controls: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 16
  },
  dateButtons: {
    flexDirection: 'row',
    gap: 8
  },
  dateBtn: {
    paddingHorizontal: 14,
    paddingVertical: 6,
    borderRadius: 6,
    backgroundColor: 'rgba(255,255,255,0.05)',
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.1)'
  },
  dateBtnActive: {
    backgroundColor: 'rgba(168, 85, 247, 0.2)',
    borderColor: '#A855F7'
  },
  dateBtnText: {
    color: theme.colors.textSecondary,
    fontSize: 13,
    fontWeight: '600'
  },
  dateBtnTextActive: {
    color: '#A855F7'
  },
  refreshBtn: {
    padding: 8
  },
  tabs: {
    flexDirection: 'row',
    gap: 8,
    marginBottom: 20
  },
  tab: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 8,
    backgroundColor: 'rgba(255,255,255,0.05)'
  },
  tabActive: {
    backgroundColor: 'rgba(168, 85, 247, 0.15)'
  },
  tabText: {
    color: theme.colors.textSecondary,
    fontSize: 13,
    fontWeight: '500'
  },
  tabTextActive: {
    color: '#A855F7'
  },
  tabBadge: {
    backgroundColor: '#EF4444',
    borderRadius: 10,
    paddingHorizontal: 6,
    paddingVertical: 1,
    marginLeft: 4
  },
  tabBadgeText: {
    color: '#FFF',
    fontSize: 10,
    fontWeight: '700'
  },
  section: {
    gap: 16
  },
  sectionHeader: {
    marginBottom: 4
  },
  sectionTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: theme.colors.textPrimary
  },
  sectionSubtitle: {
    fontSize: 13,
    color: theme.colors.textSecondary,
    marginTop: 2
  },
  // Stats Row
  statsRow: {
    flexDirection: 'row',
    gap: 10
  },
  statBox: {
    flex: 1,
    backgroundColor: 'rgba(255,255,255,0.05)',
    borderRadius: 10,
    padding: 14,
    alignItems: 'center',
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.1)'
  },
  statNumber: {
    fontSize: 26,
    fontWeight: '700',
    color: theme.colors.textPrimary
  },
  statLabel: {
    fontSize: 11,
    color: theme.colors.textSecondary,
    marginTop: 2,
    textTransform: 'uppercase',
    letterSpacing: 0.5
  },
  // Cards
  card: {
    backgroundColor: 'rgba(255,255,255,0.05)',
    borderRadius: 10,
    padding: 14,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.1)'
  },
  cardTitle: {
    fontSize: 13,
    fontWeight: '600',
    color: theme.colors.textSecondary,
    marginBottom: 10,
    textTransform: 'uppercase',
    letterSpacing: 0.5
  },
  gradeBar: {
    flexDirection: 'row',
    height: 22,
    borderRadius: 11,
    overflow: 'hidden',
    marginBottom: 10
  },
  gradeSegment: {
    alignItems: 'center',
    justifyContent: 'center'
  },
  gradeSegmentText: {
    color: '#FFF',
    fontSize: 10,
    fontWeight: '700'
  },
  gradeLegend: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 10
  },
  legendItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4
  },
  legendDot: {
    width: 8,
    height: 8,
    borderRadius: 4
  },
  legendText: {
    fontSize: 12,
    color: theme.colors.textSecondary
  },
  // Insights Row
  insightsRow: {
    flexDirection: 'row',
    gap: 10
  },
  insightCard: {
    flex: 1,
    backgroundColor: 'rgba(255,255,255,0.05)',
    borderRadius: 10,
    padding: 12,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.1)'
  },
  insightCardIssues: {
    borderColor: 'rgba(245, 158, 11, 0.2)'
  },
  insightCardPositives: {
    borderColor: 'rgba(34, 197, 94, 0.2)'
  },
  insightHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    marginBottom: 8
  },
  insightTitle: {
    fontSize: 12,
    fontWeight: '600',
    color: theme.colors.textSecondary
  },
  insightRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 4
  },
  insightText: {
    flex: 1,
    fontSize: 11,
    color: theme.colors.textPrimary,
    marginRight: 8
  },
  insightPct: {
    fontSize: 11,
    fontWeight: '600',
    color: '#F59E0B'
  },
  // AI Card
  aiCard: {
    backgroundColor: 'rgba(168, 85, 247, 0.08)',
    borderRadius: 10,
    padding: 14,
    borderWidth: 1,
    borderColor: 'rgba(168, 85, 247, 0.25)'
  },
  aiHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginBottom: 10
  },
  aiTitle: {
    fontSize: 13,
    fontWeight: '600',
    color: '#A855F7'
  },
  aiText: {
    fontSize: 13,
    color: theme.colors.textPrimary,
    lineHeight: 20,
    marginBottom: 4
  },
  // Recent Queries Section
  recentSection: {
    marginTop: 8
  },
  recentHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 12
  },
  recentTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: theme.colors.textPrimary
  },
  recentCount: {
    fontSize: 13,
    color: theme.colors.textSecondary
  },
  queryList: {
    gap: 10
  },
  // Query Card
  queryCard: {
    backgroundColor: 'rgba(255,255,255,0.05)',
    borderRadius: 10,
    padding: 12,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.08)'
  },
  queryHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 6
  },
  queryScores: {
    flexDirection: 'row',
    gap: 6
  },
  scorePill: {
    paddingHorizontal: 8,
    paddingVertical: 3,
    borderRadius: 6
  },
  scoreText: {
    fontSize: 13,
    fontWeight: '700'
  },
  gradePill: {
    paddingHorizontal: 6,
    paddingVertical: 3,
    borderRadius: 4
  },
  gradeText: {
    fontSize: 11,
    fontWeight: '700'
  },
  adminPill: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 3,
    paddingHorizontal: 6,
    paddingVertical: 3,
    borderRadius: 4,
    backgroundColor: 'rgba(168, 85, 247, 0.2)'
  },
  adminPillText: {
    fontSize: 11,
    fontWeight: '600',
    color: '#A855F7'
  },
  queryTime: {
    fontSize: 11,
    color: theme.colors.textTertiary
  },
  queryText: {
    fontSize: 14,
    color: theme.colors.textPrimary,
    marginBottom: 8,
    fontStyle: 'italic'
  },
  queryMeta: {
    flexDirection: 'row',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 4
  },
  metaItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4
  },
  metaText: {
    fontSize: 11,
    color: theme.colors.textTertiary
  },
  metaDivider: {
    width: 1,
    height: 10,
    backgroundColor: 'rgba(255,255,255,0.1)',
    marginHorizontal: 4
  },
  cacheTag: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 3,
    paddingHorizontal: 5,
    paddingVertical: 2,
    borderRadius: 4,
    backgroundColor: 'rgba(107, 114, 128, 0.2)'
  },
  cacheTagHit: {
    backgroundColor: 'rgba(34, 197, 94, 0.15)'
  },
  cacheText: {
    fontSize: 10,
    color: '#6B7280'
  },
  feedbackRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 6,
    marginTop: 8
  },
  issueChip: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingHorizontal: 8,
    paddingVertical: 4,
    backgroundColor: 'rgba(245, 158, 11, 0.15)',
    borderRadius: 6,
    maxWidth: '48%'
  },
  issueChipText: {
    fontSize: 10,
    color: '#F59E0B',
    flex: 1
  },
  positiveChip: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingHorizontal: 8,
    paddingVertical: 4,
    backgroundColor: 'rgba(34, 197, 94, 0.15)',
    borderRadius: 6,
    maxWidth: '48%'
  },
  positiveChipText: {
    fontSize: 10,
    color: '#22C55E',
    flex: 1
  },
  adminNotesBox: {
    marginTop: 8,
    padding: 8,
    backgroundColor: 'rgba(168, 85, 247, 0.1)',
    borderRadius: 6
  },
  adminNotesLabel: {
    fontSize: 10,
    color: '#A855F7',
    fontWeight: '600',
    marginBottom: 2
  },
  adminNotesText: {
    fontSize: 11,
    color: theme.colors.textSecondary
  },
  showMoreBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    paddingVertical: 12,
    marginTop: 8
  },
  showMoreText: {
    fontSize: 13,
    color: '#A855F7',
    fontWeight: '500'
  },
  // Empty States
  emptyState: {
    alignItems: 'center',
    padding: 30,
    backgroundColor: 'rgba(255,255,255,0.03)',
    borderRadius: 10
  },
  emptyText: {
    color: theme.colors.textSecondary,
    fontSize: 14,
    marginTop: 10
  },
  emptySubtext: {
    color: theme.colors.textTertiary,
    fontSize: 12,
    marginTop: 4
  },
  emptyStateGood: {
    alignItems: 'center',
    padding: 40
  },
  emptyStateTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: '#22C55E',
    marginTop: 12
  },
  emptyStateText: {
    fontSize: 14,
    color: theme.colors.textSecondary,
    marginTop: 4
  },
  // Charts
  chartContainer: {
    flexDirection: 'row',
    height: 100,
    gap: 4,
    alignItems: 'flex-end',
    paddingBottom: 30,
    backgroundColor: 'rgba(255,255,255,0.03)',
    borderRadius: 10,
    padding: 12,
    paddingTop: 10
  },
  chartBar: {
    flex: 1,
    alignItems: 'center'
  },
  chartBarFill: {
    width: '80%',
    borderRadius: 3,
    minHeight: 4
  },
  chartBarScore: {
    fontSize: 8,
    color: theme.colors.textSecondary,
    fontWeight: '600',
    marginTop: 2
  },
  chartBarDate: {
    fontSize: 7,
    color: theme.colors.textTertiary,
    marginTop: 1
  },
  dailyList: {
    gap: 6
  },
  dailyRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: 10,
    paddingHorizontal: 12,
    backgroundColor: 'rgba(255,255,255,0.03)',
    borderRadius: 8
  },
  dailyDate: {
    fontSize: 13,
    color: theme.colors.textPrimary,
    fontWeight: '500'
  },
  dailyStats: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10
  },
  dailyQueries: {
    fontSize: 12,
    color: theme.colors.textSecondary
  },
  dailyScorePill: {
    paddingHorizontal: 8,
    paddingVertical: 3,
    borderRadius: 6
  },
  dailyScoreText: {
    fontSize: 12,
    fontWeight: '700'
  }
});

export default QueryAnalyticsTab;
